import { showToast } from '@cleanlab/design-system/components'
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { type AxiosResponse } from 'axios'
import { useCallback } from 'react'

import {
  addProjectEvalMutation,
  createProjectMutation,
  deleteProjectMutation,
  getProjectAnalyticsOptions,
  getProjectEvalsOptions,
  getProjectEvalsQueryKey,
  getProjectGuardrailsNextOptions,
  getProjectGuardrailsNextQueryKey,
  getProjectOptions,
  getProjectQueryKey,
  getProjectWithUnaddressedCountsOptions,
  inviteSmeMutation,
  listProjectsOptions,
  listProjectsQueryKey,
  removeProjectEvalMutation,
  sendGuardrailNotificationManualProjectMutation,
  sendOutdatedRemediationsNotificationManualProjectMutation,
  updateProjectEvalMutation,
  updateProjectMutation,
  updateProjectTemplateStatusMutation,
} from '@/client/@tanstack/react-query.gen'
import { exportProject } from '@/client/sdk.gen'
import {
  type CustomEvalCreateOrUpdateSchema,
  type DefaultEvalUpdateSchema,
  type Eval,
  type GetProjectEvalsData,
  type ListGuardrailsNextReturnSchema,
  type ProjectCreateInputSchema,
  type ProjectUpdateSchema,
} from '@/client/types.gen'
import { TOAST_OPTS, TOAST_PROPS } from '@/global/consts'
import { getErrorMessage } from '@/global/utils'

import { EVAL_TYPE_LABELS } from '../(authenticated)/projects/consts'
import { EVAL_ITEM_LABELS, type EvalItem } from '../(authenticated)/projects/types'
import { downloadFileFromAxiosResponse } from './utils/downloadFile'

const invalidateProjectSettings = ({
  projectId,
  queryClient,
  organizationId,
}: {
  projectId: string
  queryClient: QueryClient
  organizationId: string
}) => {
  queryClient.invalidateQueries({
    queryKey: getProjectQueryKey({ path: { project_id: projectId } }),
  })
  queryClient.invalidateQueries({
    queryKey: listProjectsQueryKey({ query: { organization_id: organizationId } }),
  })
}

export const useGetProject = (projectId: string, opts = {}) => {
  return useQuery({
    ...getProjectOptions({ path: { project_id: projectId } }),
    ...opts,
  })
}

export const useGetProjectWithUnaddressedCounts = (projectId: string, opts = {}) => {
  return useQuery({
    ...getProjectWithUnaddressedCountsOptions({ path: { project_id: projectId } }),
    ...opts,
  })
}

export const useGetProjects = (organizationId: string, opts = {}) => {
  return useQuery({
    ...listProjectsOptions({ query: { organization_id: organizationId } }),
    enabled: Boolean(organizationId),
    ...opts,
  })
}

export const useGetProjectsSuspense = ({
  organizationId,
  limit,
  offset,
  includeUnaddressedCounts = true,
  sortBy = 'updated_at',
  sortOrder = 'desc',
  query,
  opts = {},
}: {
  organizationId: string
  limit: number
  offset: number
  includeUnaddressedCounts: boolean
  sortBy: 'created_at' | 'updated_at'
  sortOrder: 'asc' | 'desc'
  query?: string
  opts?: Record<string, unknown>
}) => {
  return useSuspenseQuery({
    ...listProjectsOptions({
      query: {
        organization_id: organizationId,
        limit,
        offset,
        include_unaddressed_counts: includeUnaddressedCounts,
        sort: sortBy,
        order: sortOrder,
        query,
      },
    }),
    ...opts,
  })
}

export const useCreateProject = ({ organizationId }: { organizationId: string }) => {
  const queryClient = useQueryClient()
  const createProjectMutationResult = useMutation({
    ...createProjectMutation(),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: listProjectsQueryKey({ query: { organization_id: organizationId } }),
      })
    },
  })
  const createProject = async (body: ProjectCreateInputSchema) => {
    return createProjectMutationResult.mutateAsync({ body })
  }

  return { createProject, ...createProjectMutationResult }
}

export const useUpdateProject = ({ organizationId }: { organizationId: string }) => {
  const queryClient = useQueryClient()
  const updateProjectMutationResult = useMutation({
    ...updateProjectMutation(),
    onSettled: (_data, _error, variables) => {
      invalidateProjectSettings({
        projectId: variables.path?.project_id ?? '',
        queryClient,
        organizationId,
      })
    },
  })
  const updateProject = async (id: string, body: ProjectUpdateSchema) => {
    return updateProjectMutationResult.mutateAsync({ path: { project_id: id }, body })
  }

  return { updateProject, ...updateProjectMutationResult }
}

export const useUpdateProjectTemplateStatus = ({
  projectId,
  organizationId,
}: {
  projectId: string
  organizationId: string
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    ...updateProjectTemplateStatusMutation({
      path: { project_id: projectId },
    }),
    onSuccess: (data) => {
      // Update the cache for getProject
      queryClient.setQueryData(
        getProjectQueryKey({ path: { project_id: projectId } }),
        (oldData: unknown) => {
          if (oldData && typeof oldData === 'object') {
            return {
              ...oldData,
              is_template: data.is_template,
            }
          }
          return oldData
        }
      )

      // Update the cache for listProjects
      queryClient.setQueryData(
        listProjectsQueryKey({
          query: { organization_id: data.organization_id },
        }),
        (oldData: unknown) => {
          if (
            oldData &&
            typeof oldData === 'object' &&
            'projects' in oldData &&
            Array.isArray(oldData.projects)
          ) {
            return {
              ...oldData,
              projects: oldData.projects.map((project: unknown) =>
                project &&
                typeof project === 'object' &&
                'id' in project &&
                project.id === projectId
                  ? { ...project, is_template: data.is_template }
                  : project
              ),
            }
          }
          return oldData
        }
      )
    },
    onSettled: (_data, _error) => {
      invalidateProjectSettings({
        queryClient,
        projectId,
        organizationId,
      })
    },
  })
}

export const useDeleteProject = ({ organizationId }: { organizationId: string }) => {
  const queryClient = useQueryClient()
  const deleteProjectMutationResult = useMutation({
    ...deleteProjectMutation(),
    onSettled: (_data, _error, variables) => {
      invalidateProjectSettings({
        queryClient,
        projectId: variables.path?.project_id ?? '',
        organizationId,
      })
    },
  })
  const deleteProject = async (id: string) => {
    return deleteProjectMutationResult.mutateAsync({ path: { project_id: id } })
  }

  return { deleteProject, ...deleteProjectMutationResult }
}

export const useExportProject = () => {
  const exportProjectFile = async (projectId: string) => {
    const response = (await exportProject({
      path: { project_id: projectId },
      throwOnError: true,
    })) as AxiosResponse
    downloadFileFromAxiosResponse(response, `project_${projectId}_export.json`)
  }
  return { exportProject: exportProjectFile }
}

export const useGetProjectAnalytics = ({
  projectId,
  startDate,
  endDate,
  metadataFilters,
}: {
  projectId: string
  startDate: number | null
  endDate: number | null
  metadataFilters: Record<string, (string | null)[] | null> | null
}) => {
  return useQuery({
    ...getProjectAnalyticsOptions({
      path: { project_id: projectId },
      query: {
        start: startDate,
        end: endDate,
        metadata_filters: metadataFilters ? JSON.stringify(metadataFilters) : null,
      },
    }),
  })
}

/**
 * Invalidates React Query caches for project evaluations and guardrails.
 * This triggers a refetch of both the project evals and guardrails data.
 *
 * @param projectId - The ID of the project whose eval and guardrail queries should be invalidated
 * @param queryClient - The React Query client instance used to invalidate the queries
 */
export const invalidateProjectEvals = (projectId: string, queryClient: QueryClient) => {
  queryClient.invalidateQueries({
    queryKey: getProjectEvalsQueryKey({ path: { project_id: projectId } }),
  })
  queryClient.invalidateQueries({
    queryKey: getProjectGuardrailsNextQueryKey({ path: { project_id: projectId } }),
  })
}

export const useGetProjectEvals = (projectId: string, query: GetProjectEvalsData['query']) => {
  return useQuery({
    ...getProjectEvalsOptions({
      path: { project_id: projectId },
      query,
    }),
  })
}

export const useGetProjectGuardrails = (projectId: string) => {
  return useQuery({
    ...getProjectGuardrailsNextOptions({
      path: { project_id: projectId },
    }),
    /*
     * Backend returns GuardrailItemSchema[] which has essentially the same shape as EvalItem[]
     * GuardrailItemSchema is:
     *   {
     *     type: 'semantic' | 'deterministic'
     *     id: string
     *     value: Eval | DeterministicEval
     *   }
     * But EvalItem is a discriminated union:
     *   | { type: 'semantic', id: string, value: Eval }
     *   | { type: 'deterministic', id: string, value: DeterministicEval }
     * Casting to EvalItem[] allows us to narrow the type of `value` to just one of its
     * two possibilities based on the value of the `type` property.
     *
     * @example
     * ```tsx
     * const guardrailItems: EvalItem[] = (projectGuardrails?.guardrails ?? []) as EvalItem[]
     * guardrailItems.forEach((item) => {
     *   if (item.type === 'semantic') {
     *     console.log(item.value.eval_key)
     *   } else {
     *     console.log(item.value.id)
     *   }
     * })
     * ```
     */
    select: (data) =>
      data as Omit<ListGuardrailsNextReturnSchema, 'guardrails'> & { guardrails: EvalItem[] },
  })
}

export const useAddProjectEval = ({ projectId }: { projectId: string }) => {
  const queryClient = useQueryClient()
  const addProjectEvalMutationResult = useMutation({
    ...addProjectEvalMutation(),
    onSettled: () => {
      invalidateProjectEvals(projectId, queryClient)
    },
    onSuccess: (_, variables) => {
      showToast(
        TOAST_PROPS.create.success({
          typeLabel: EVAL_ITEM_LABELS.eval.itemType,
          itemName: variables.body.name,
        }),
        TOAST_OPTS
      )
    },
  })
  const addProjectEval = async (body: CustomEvalCreateOrUpdateSchema) => {
    return addProjectEvalMutationResult.mutateAsync({
      path: { project_id: projectId },
      body,
    })
  }

  return { addProjectEval, ...addProjectEvalMutationResult }
}

export const useUpdateProjectEval = ({ projectId }: { projectId: string }) => {
  const queryClient = useQueryClient()
  const updateProjectEvalMutationResult = useMutation({
    ...updateProjectEvalMutation(),
    onSettled: () => {
      invalidateProjectEvals(projectId, queryClient)
    },
  })
  const updateProjectEval = async (
    evalKey: string,
    body: CustomEvalCreateOrUpdateSchema | DefaultEvalUpdateSchema
  ) => {
    return updateProjectEvalMutationResult.mutateAsync({
      path: { project_id: projectId, eval_key: evalKey },
      body,
    })
  }

  return { updateProjectEval, ...updateProjectEvalMutationResult }
}

export const useRemoveProjectEval = ({ projectId }: { projectId: string }) => {
  const queryClient = useQueryClient()
  const removeProjectEvalMutationResult = useMutation({
    ...removeProjectEvalMutation(),
    onSettled: () => {
      invalidateProjectEvals(projectId, queryClient)
    },
  })
  const { mutateAsync, mutate: _mutate, ...rest } = removeProjectEvalMutationResult

  const removeEval = useCallback(
    async (evaluation: Eval) => {
      const itemType = evaluation.should_guardrail
        ? EVAL_ITEM_LABELS.guardrail.itemType
        : EVAL_ITEM_LABELS.eval.itemType
      const typeLabel = `${EVAL_TYPE_LABELS.probabilistic} ${itemType}`

      return mutateAsync(
        {
          path: { project_id: projectId, eval_key: evaluation.eval_key },
        },
        {
          onSuccess: () => {
            showToast(
              TOAST_PROPS.delete.success({ typeLabel, itemName: evaluation.name }),
              TOAST_OPTS
            )
          },
          onError: (error) => {
            showToast(
              TOAST_PROPS.delete.error({
                typeLabel,
                itemName: evaluation.name,
                errorMessage: getErrorMessage(error),
              }),
              TOAST_OPTS
            )
          },
        }
      )
    },
    [projectId, mutateAsync]
  )
  return { removeEval, ...rest }
}

// Current invite endpoint for query logs and remediations
export const useInviteSme = () => {
  return useMutation({
    ...inviteSmeMutation(),
    onSuccess: (_, variables) => {
      showToast(
        {
          heading: 'SME notified',
          description: `Email sent to ${variables.body?.email}`,
          status: 'success',
        },
        TOAST_OPTS
      )
    },
    onError: (error) => {
      showToast(
        {
          heading: 'Error notifying SME',
          description: getErrorMessage(error),
          status: 'warning',
        },
        TOAST_OPTS
      )
    },
  })
}

export const useNotifyGuardrail = () => {
  return useMutation({
    ...sendGuardrailNotificationManualProjectMutation(),
    onSuccess: (_, variables) => {
      showToast({
        status: 'success',
        heading: 'Alert Email Sent',
        description: `Guardrail threshold alert sent to ${variables.body?.user_email} for ${variables.body?.guardrail_name}`,
        ...TOAST_OPTS,
      })
    },
  })
}

export const useNotifyOutdatedRemediations = () => {
  return useMutation({
    ...sendOutdatedRemediationsNotificationManualProjectMutation(),
    onSuccess: (_, variables) => {
      showToast({
        status: 'success',
        heading: 'Outdated Remediations Email Sent',
        description: `Found ${variables.body?.num_remediations} remediations older than ${variables.body?.threshold_days} days, email sent to ${variables.body?.user_email}`,
        ...TOAST_OPTS,
      })
    },
  })
}
